'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'
import { calcConcreteSlab } from '@/lib/calculators/formulas'

// Public, no-auth concrete calculator. Reuses the exact same slab math as the
// in-app estimator (calcConcreteSlab) so results match the product, but ships
// self-contained marketing-light styling (no app theme tokens) and swaps the
// "save to notebook" action for a free-account CTA. This is the SEO spearhead:
// a real, useful, indexable tool that converts search traffic into signups.

function num(value: string): number {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

function fmt(value: number, decimals = 2): string {
  return value.toLocaleString('en-ZA', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

function fmtCurrency(value: number): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(value)
}

function Field({
  label, value, onChange, unit, hint, step = '1',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  unit: string
  hint?: string
  step?: string
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <span className="mt-1 flex overflow-hidden rounded-xl border border-slate-300 bg-white focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none tabular-nums"
        />
        <span className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-500">{unit}</span>
      </span>
      {hint ? <span className="mt-1 block text-xs text-slate-400">{hint}</span> : null}
    </label>
  )
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-3 px-4 ${strong ? 'py-3' : 'py-2.5'}`}>
      <span className={`text-sm ${strong ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{label}</span>
      <span className={`shrink-0 tabular-nums ${strong ? 'text-lg font-bold text-orange-600' : 'text-sm font-semibold text-slate-900'}`}>{value}</span>
    </div>
  )
}

export function ConcreteCalculatorTool() {
  const [length, setLength] = useState('10')
  const [width, setWidth] = useState('5')
  const [thickness, setThickness] = useState('100')
  const [waste, setWaste] = useState('7')
  const [price, setPrice] = useState('2200')

  const result = useMemo(() => calcConcreteSlab({
    length: num(length),
    width: num(width),
    thicknessMm: num(thickness),
    wasteFactor: 1 + num(waste) / 100,
    concreteM3Price: num(price),
    // Volume + concrete cost only depend on the fields above; the rest are
    // required by the shared type but don't affect the figures we surface here.
    labourRatePerHour: 0,
    reinforcingKgPerM2: 0,
    steelPricePerKg: 0,
    finishType: 'broom',
  }), [length, width, thickness, waste, price])

  // A 40 kg bag of concrete mix yields ~0.018 m³ — useful for small hand-mixed pours.
  const bags40kg = Math.ceil(result.volumeOrder / 0.018)

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_380px] md:items-start">
      {/* Inputs */}
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Slab dimensions</h2>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Field label="Length" value={length} onChange={setLength} unit="m" />
            <Field label="Width" value={width} onChange={setWidth} unit="m" />
            <Field label="Thickness" value={thickness} onChange={setThickness} unit="mm" step="10" hint="75mm min driveway" />
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Waste &amp; price</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Waste allowance" value={waste} onChange={setWaste} unit="%" hint="5–10% typical" />
            <Field label="Concrete price" value={price} onChange={setPrice} unit="R/m³" hint="R1,800–R2,800 delivered" />
          </div>
        </div>
      </div>

      {/* Results + CTA */}
      <div className="space-y-4 md:sticky md:top-24">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your concrete estimate</p>
          </div>
          <div className="divide-y divide-slate-100">
            <Row label="Slab area" value={`${fmt(result.areaM2, 1)} m²`} />
            <Row label="Concrete volume" value={`${fmt(result.volumeRaw)} m³`} />
            <Row label={`Order quantity (+${num(waste)}% waste)`} value={`${fmt(result.volumeOrder, 2)} m³`} />
            <Row label="≈ 40 kg bags (hand-mix)" value={`${bags40kg} bags`} />
            <Row label="Estimated concrete cost" value={fmtCurrency(result.concreteM3Cost)} strong />
          </div>
        </div>

        <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4">
          <p className="text-sm font-semibold text-slate-900">Turn this into a client-ready quote</p>
          <p className="mt-1 text-sm text-slate-600">
            Add labour, rebar, and your margin, then send a professional estimate from your phone — free, no card.
          </p>
          <Link
            href="/signup"
            className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-orange-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-orange-700"
          >
            Create a free account →
          </Link>
        </div>
        <p className="text-center text-xs text-slate-400">
          Estimate only. Confirm volumes with your supplier and check site conditions before ordering.
        </p>
      </div>
    </div>
  )
}
